import { type Nullable, PastaError } from "@pasta/box-v2";
import { InvalidTokenError } from "@pasta/front-auth-v2";
import dayjs from "dayjs";
import { isArray, isNil } from "lodash";
import type { ComputedRef } from "vue";
import { type LocationQueryValue } from "vue-router";
import { type LetterFileDto } from "../../dto/letter-file.dto";
import { genderize, useGenderize } from "@/app/utils/gender";

export const FormToken = Symbol("letter-file-form");

export interface FormProvide {
  refresh: () => Promise<void>;
  letterFile: ComputedRef<Nullable<LetterFileDto>>;
}

// An expired session must reach front-auth's InvalidTokenErrorManager, which a PastaError wrapper would hide
export function wrapUnlessSessionExpired(error: unknown): never {
  if (error instanceof InvalidTokenError) {
    throw error;
  }

  throw new PastaError({
    message: (error as { message?: string } | undefined)?.message || "",
    publicMessage: "Une erreur est survenue lors de la récupération des données",
    level: "error",
  });
}

export function getBoolFromQuery(query: LocationQueryValue | LocationQueryValue[]) {
  if (!query) {
    return false;
  }

  if (isArray(query)) {
    return parseBool(query[0] as string);
  }

  const value = parseBool(query);
  return value;
}

function parseBool(value: string): boolean {
  return value.toLowerCase() === "true";
}

export function getArrayStringFromQuery(query: LocationQueryValue | LocationQueryValue[]): string[] {
  if (!query) {
    return [];
  }

  if (isArray(query)) {
    return query.filter(value => !isNil(value)) as string[];
  }

  return [query];
}

export function canGoNext(letterFile: LetterFileDto): boolean {
  const genderizeMe = useGenderize();
  const next = letterFile.downstream?.find(el => el.position === 1);
  const hasDocs = letterFile?.folders?.find(folder => (folder.documents || []).length >= 1);
  let goNext = true;

  if (letterFile?.downstream?.length === 0) {
    goNext = confirm(
      "Ce parapheur va être archivé (aucune étape dans le flux descendant). Si des commentaires privés sont présents, ils seront supprimés. Confirmer ?"
    );
  } else if (letterFile?.comments?.length === 0 && !hasDocs) {
    const nextName = next?.name || "Archivé";
    goNext = confirm(
      `Ce parapheur ne contient aucun document ou commentaire, êtes vous sûr(e) de vouloir l'envoyer à ${nextName} ?`
    );
  }

  if (goNext && next?.absence) {
    goNext = confirm(
      `${next.name} est ${genderize("absent", next.civility)}. Êtes-vous ${genderizeMe("sûr")} de vouloir lui envoyer ce parapheur ?`
    );
  }

  return goNext;
}

export function canEditAfterArchival(letterFile: LetterFileDto, file?: { creationDate: string }): boolean {
  if (!letterFile?.unArchivedDate) return true;
  const unArchivedDate = dayjs(letterFile.unArchivedDate);

  return letterFile.isAuthor || (file !== undefined && !dayjs(file.creationDate).isBefore(unArchivedDate));
}
